import {
  MutationHandlingServiceV2,
  NotificationService,
  ObservableHandlingService,
  ObservableRef,
  QueryHandlingService,
  WatchQueryRef,
} from '@akebono/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { combineLatest, iif, interval, Observable, of, ReplaySubject, Subject, timer } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  first,
  map,
  pluck,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import {
  AutoLotView,
  BID_CONDITION_UTILIZATION,
  isOtherCountriesCondition,
  normalizeAuctionStartDatetime,
  SPECIAL_EQUIPMENT_ID,
  validateAllFormFields,
} from '../../../const';
import { BidConditionService } from '../../../services/bid-condition.service';
import { BiddingRulesService } from '../../../services/bidding-rules.service';
import { Limits, LimitsService } from '../../../services/limits.service';
import {
  BidCondition,
  BidCreateGQL,
  BidCreateInput,
  BidCreatePayload,
  BidDataGQL,
  BidDataQuery,
  BidGroup,
  BidGroupCreateGQL,
  BidGroupCreateInput,
  CurrentUserMinimalGQL,
  CurrentUserMinimalQuery,
  CurrentUserMinimalQueryVariables,
  LotAutoTranslationCreateGQL,
  LotSource,
  LotTypeEnum,
  PlaceBidHint,
  UserLotQuery,
} from '../../graphql/service/graphql-auto-main-service';
import { Auction } from '../../graphql/service/graphql-stats-auto-service';
import { AlertService } from '../alerts-list/alert.service';
import { SettingsModalComponent } from '../settings-modal/components/settings-modal/settings-modal.component';
import { NavisRulesModalComponent } from './components/navis-rules-modal/navis-rules-modal.component';
import { PriorityPassInfoComponent } from './components/priority-pass-info/priority-pass-info.component';
import { SetBirthdateModalComponent } from './components/set-birthdate-modal/set-birthdate-modal.component';
import { BidData } from './types/lot-bidding-modal.type';
import {
  getLotBiddingDeadline,
  isLotTradingClosed,
  isNegotiationsLot,
  NEGOTIATIONS_OVERSTEP,
} from '../../../utils/lot-trading.util';
import { AuctionService } from 'src/app/services/auction.service';
import { LotBiddingModalGuideService } from '../../guides/lot-bidding-modal-guide/services/lot-bidding-modal-guide.service';
import { RemoveNewGroupOptions } from '../../guides/lot-bidding-modal-guide/types/start-lot-bidding-modal-guide-params.type';

@Component({
  selector: 'app-lot-bidding-modal',
  templateUrl: './lot-bidding-modal.component.html',
  styleUrls: ['./lot-bidding-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotBiddingModalComponent implements OnInit, OnDestroy {
  @Input() lot: AutoLotView;
  @Input() lotType: LotTypeEnum;
  @Input() lotSource: LotSource;
  @Input() limits: Limits;
  @Input() hasTranslations = false;
  @Input() hasManualTranslations = false;
  @Input() withLimitsOverride = false;
  @Input() canRecognizeAuctionList = false;
  @Input() renderCalculator = false;
  @Input() currentUserLastBid?: UserLotQuery['lot']['currentUserLastBid'];
  @Output() afterSubmit = new EventEmitter<boolean>();

  biddingDataQueryRef: ObservableRef<BidData> = null;
  isPlacingBid$ = of(false);
  bidMayNotBeProcessed = DateTime.now().setZone('Asia/Tokyo').get('hour') >= 18;

  submitDisabled$ = of(true);
  maxBidLimitReached$ = new Subject<boolean>();
  canOrderPriorityPass$ = of(false);
  maxBidAmountLimitExceeded$ = of(false);
  bidDelete1700prohibited = false;
  bidDelete1100prohibited = false;

  placeConfirmationNotNeed$: Observable<boolean>;
  bidMakeSense$: Observable<boolean>;
  isHighImportDutyLot$: Observable<boolean>;
  isSanctionedUtilization$: Observable<boolean>;
  bidMakeSenseChange$ = new ReplaySubject<boolean>(1);

  isNewGroup = false;

  form: FormGroup;
  isDisassembly = false;

  deadlineLotTime = 0;

  SPECIAL_EQUIPMENT_ID = SPECIAL_EQUIPMENT_ID;

  isEndSoon$ = of(false);
  isActiveLot$ = of(false);
  tradingClosed$: Observable<boolean>;
  tradingOpen$: Observable<boolean>;
  private effectiveBidAmountLimit: number;

  get biddingDeadlineTime(): number {
    return getLotBiddingDeadline(this.lot).toMillis();
  }

  get isFirstNegotiationsBid(): boolean {
    return isNegotiationsLot(this.lot) && !this.currentUserLastBid;
  }

  get negotiationsMinimum(): number {
    return isNegotiationsLot(this.lot)
      ? Number(this.lot.negotiationsPrice) + NEGOTIATIONS_OVERSTEP
      : 0;
  }

  formatterYen = (value: number) => `¥ ${value && value.toLocaleString('en-US')}`;
  parserYen = (value: string) => value.replace(/\D/g, '');

  queryRef: WatchQueryRef<CurrentUserMinimalQuery, CurrentUserMinimalQueryVariables>;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    public modal: NzModalRef,
    private bcs: BidConditionService,
    private brs: BiddingRulesService,
    private translate: TranslateService,
    private limitService: LimitsService,
    private alertService: AlertService,
    private lotBiddingModalGuideService: LotBiddingModalGuideService,
    private modalService: NzModalService,
    private notification: NotificationService,
    private qhs: QueryHandlingService,
    private mhs: MutationHandlingServiceV2,
    private ohs: ObservableHandlingService,
    private auctionService: AuctionService,
    private bidDataGQL: BidDataGQL,
    private bidCreateGQL: BidCreateGQL,
    private bidGroupCreateGQL: BidGroupCreateGQL,
    private currentUserMinimalGQL: CurrentUserMinimalGQL,
    private lotAutoTranslationCreateGQL: LotAutoTranslationCreateGQL,
    private cdr: ChangeDetectorRef,
  ) {
    this.initTradingClosed();
    this.queryRef = this.qhs.watch(this.currentUserMinimalGQL);
  }

  private initTradingClosed(): void {
    this.tradingClosed$ = timer(0, 1000).pipe(
      map(() => isLotTradingClosed(this.lot)),
      distinctUntilChanged(),
      tap((closed) => {
        if (closed) {
          this.lotBiddingModalGuideService.forceClose();
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.tradingOpen$ = this.tradingClosed$.pipe(map((closed) => !closed));
  }

  private initTiming(): void {
    const auctionStart = normalizeAuctionStartDatetime(this.lot.time);
    this.deadlineLotTime = auctionStart.toMillis();
    const nowTimer$ = timer(0, 1000).pipe(map(() => DateTime.now()));

    this.isActiveLot$ = nowTimer$.pipe(
      map((now) => now <= auctionStart),
      distinctUntilChanged(),
    );

    this.isEndSoon$ = nowTimer$.pipe(
      map((now) => now >= auctionStart.minus({ minute: 5 })),
      distinctUntilChanged(),
    );
  }

  ngOnInit(): void {
    this.initTiming();
    this.createForm();
    this.bidDelete1700prohibited = this.limitService.check1700BidDeleteBorder(
      DateTime.fromISO(this.lot.time, { zone: 'Asia/Tokyo' }),
    );
    this.bidDelete1100prohibited = this.limitService.check1100BidDeleteBorder(
      DateTime.fromISO(this.lot.time, { zone: 'Asia/Tokyo' }),
    );

    const bidData$ = combineLatest([
      this.auctionService.getByName(this.lot.auctionName),
      this.brs.checkIfCanBidWithGroupsToAuction(this.lot.auctionName),
    ]).pipe(
      switchMap(([auction, isCanGroupBids]) =>
        this.qhs
          .fetch(
            this.bidDataGQL,
            {
              date: DateTime.fromISO(this.lot.time, { zone: 'Asia/Tokyo' }).startOf('day').toISO(),
              auctionId: auction?.id?.toString(),
              lotType: this.lotType,
            },
            'network-only',
          )
          .data.pipe(
            map((data) => [data, isCanGroupBids, auction] as [BidDataQuery, boolean, Auction]),
          ),
      ),
      switchMap(([data, isCanGroupBids, auction]) =>
        this.bcs
          .filterForLot(data.bidConditions, this.lot, this.lotType, data.currentUser)
          .pipe(
            map(
              (conditions) =>
                [data, conditions, isCanGroupBids, auction] as [
                  BidDataQuery,
                  BidCondition[],
                  boolean,
                  Auction,
                ],
            ),
          ),
      ),
      tap(([data, conditions]) => {
        this.effectiveBidAmountLimit = data.currentUser?.isOtherCountries
          ? null
          : data.maxBidAmount;
        const createTranslation = this.canCreateTranslation
          ? data.currentUser?.bidTranslationByDefault ?? false
          : false;
        const defaultCondition = this.bcs.getDefault(conditions, data.currentUser);
        this.resetForm(defaultCondition, createTranslation);
      }),
      map(
        ([data, conditions, isCanGroupBids, auction]) =>
          <BidData>{
            auction,
            isCanGroupBids,
            currentUser: data.currentUser,
            maxBidAmount: data.maxBidAmount,
            minBidCommission: data.minBidCommission,
            isSanctioned: this.lot?.isRestricted,
            conditions: this.bcs.sort(conditions),
            bidGroups: data.currentUser.bidGroups.nodes,
            ignoreSmallBidWarning: data.currentUser.ignoreSmallBidWarning,
            allowedLetters: this.brs.getAllowedLetters(data.currentUser),
            isToyamaUser: !data.currentUser.officeBranch.isWorldwide,
            alerts: this.alertService.filterByCountryIso(
              [...data.alerts, ...(this.lot?.isRestricted ? data.sanctionedAlerts : [])],
              data.currentUser?.countryIso,
            ),
          },
      ),
      shareReplay(),
    );

    this.biddingDataQueryRef = this.ohs.toReference(bidData$);

    this.initMaxBidAmountLimitExceeded(bidData$);
    this.initCanOrderPriorityPass(bidData$);
    this.initIsHighImportDutyLot(bidData$);
    this.initIsSanctionedUtilization(bidData$);
    this.initSubmitDisabled();
    this.initBidMakeSense();
    this.initPlaceConfirmationNotNeed();
    this.initCreateTranslationControlFieldByConditionSub();
  }

  private initMaxBidAmountLimitExceeded(bidData$: Observable<BidData>): void {
    this.maxBidAmountLimitExceeded$ = combineLatest([
      this.form.controls.amount.valueChanges,
      bidData$.pipe(pluck('currentUser', 'maxBidAmountLimit')),
    ]).pipe(
      map(([amount, maxAmount]) => {
        if (maxAmount === null) {
          return false;
        } else {
          return amount > maxAmount;
        }
      }),
    );
  }

  private initCanOrderPriorityPass(bidData$: Observable<BidData>): void {
    this.canOrderPriorityPass$ = combineLatest([
      bidData$.pipe(pluck('currentUser', 'isOtherCountries')),
      this.form.controls.conditionId.valueChanges.pipe(
        map((conditionId) => isOtherCountriesCondition({ id: conditionId })),
        tap((isNotOtherCountriesBidCondition) => {
          if (!isNotOtherCountriesBidCondition) {
            this.form.controls.needPriorityPass.setValue(false);
          }
        }),
      ),
    ]).pipe(
      map(
        ([isNotOtherCountriesPlan, isNotOtherCountriesBidCondition]) =>
          isNotOtherCountriesPlan && isNotOtherCountriesBidCondition,
      ),
      startWith(false),
    );
  }

  private initIsHighImportDutyLot(bidData$: Observable<BidData>): void {
    this.isHighImportDutyLot$ = bidData$.pipe(
      map((bidData) => {
        const { company, modelName, frame } = this.lot;
        const isRussianClient = bidData.currentUser?.countryIso?.toLowerCase() === 'ru';
        return (
          isRussianClient &&
          company.toLowerCase() === 'nissan' &&
          modelName.toLowerCase() === 'nv200' &&
          frame.toLowerCase() === 'vm20'
        );
      }),
    );
  }

  private initSubmitDisabled(): void {
    this.submitDisabled$ = combineLatest([
      this.maxBidLimitReached$,
      this.limits.timeLimitReached$,
      this.limits.lotBidsLimitReached$,
      this.limits.lotsLimitReached$,
      this.form.controls.agreement.valueChanges,
      this.form.controls.commissionAgreement.valueChanges,
      this.maxBidAmountLimitExceeded$,
      this.tradingClosed$,
      this.form.statusChanges.pipe(startWith(this.form.status)),
    ]).pipe(
      map(
        ([
          maxBidLimitReached,
          timeLimitReached,
          lotBidsLimitReached,
          bidsLimitReached,
          agreement,
          commissionAgreement,
          maxBinAmountLimitExceeded,
          tradingClosed,
          formStatus,
        ]) =>
          tradingClosed ||
          formStatus !== 'VALID' ||
          maxBinAmountLimitExceeded ||
          !agreement ||
          (maxBidLimitReached && !commissionAgreement) ||
          timeLimitReached ||
          ((lotBidsLimitReached || bidsLimitReached) && !this.withLimitsOverride),
      ),
      startWith(true),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  private initBidMakeSense(): void {
    this.bidMakeSense$ = combineLatest([
      this.bidMakeSenseChange$,
      this.biddingDataQueryRef.data.pipe(map((data) => data.ignoreSmallBidWarning)),
    ]).pipe(
      map(([bidMakeSense, ignoreSmallBidWarning]) => ignoreSmallBidWarning || bidMakeSense),
      startWith(true),
    );
  }

  private initIsSanctionedUtilization(bidData$: Observable<BidData>): void {
    this.isSanctionedUtilization$ = combineLatest([
      bidData$.pipe(map((data) => data.isSanctioned)),
      this.form.controls.conditionId.valueChanges.pipe(
        map((conditionId) => +conditionId === BID_CONDITION_UTILIZATION),
      ),
    ]).pipe(
      map(([isSanctionedLot, isUtilizationCondition]) => isSanctionedLot && isUtilizationCondition),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  private initPlaceConfirmationNotNeed(): void {
    this.placeConfirmationNotNeed$ = combineLatest([
      this.bidMakeSense$,
      this.limits.canPalaceOnlyOneBid$,
      this.isSanctionedUtilization$,
    ]).pipe(
      map(
        ([bidMakeSense, canPlaceOnlyOneBid, isSanctionedUtilization]) =>
          bidMakeSense && !canPlaceOnlyOneBid && !isSanctionedUtilization,
      ),
      distinctUntilChanged(),
    );
  }

  private initCreateTranslationControlFieldByConditionSub(): void {
    interval(1000)
      .pipe(
        startWith(0),
        map(() => !this.canCreateTranslation),
        distinctUntilChanged(),
        filter((cantCreateTranslation) => cantCreateTranslation),
        first(),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        const createTranslationControl = this.form.controls.createTranslation;
        createTranslationControl.disable();
        createTranslationControl.setValue(false);
      });
  }

  private get canCreateTranslation(): boolean {
    const now = DateTime.now().setZone('Asia/Tokyo');
    const auctionStart = normalizeAuctionStartDatetime(this.lot?.time);
    const isWorkingTime = now.get('hour') < 18;
    const isSaturdayToday = now.weekday === 6;
    const isAuctionNotToday = !auctionStart.hasSame(now, 'day');
    const isAuctionNotStarted = now <= auctionStart;
    const hasTranslations = this.hasManualTranslations;
    const isMoreThanHourBeforeAuction = !this.limitService.checkOneHourBeforeAuctionReached(
      this.lot?.time,
    );

    if (hasTranslations) {
      return false;
    } else if (isAuctionNotToday) {
      return isAuctionNotStarted;
    } else if (isSaturdayToday) {
      return now.hour < 11 && isMoreThanHourBeforeAuction;
    } else {
      return isWorkingTime && isMoreThanHourBeforeAuction;
    }
  }

  showNavisRules(): void {
    this.modalService.create({
      nzTitle: this.translate.instant('LOT_BIDDING_MODAL.NAVIS_RULES_TITLE'),
      nzContent: NavisRulesModalComponent,
    });
  }

  createForm(): void {
    this.form = this.fb.group({
      amount: [
        '',
        [
          Validators.required,
          (control) => {
            const amount = Number(control.value);
            const effectiveAmount = this.effectiveBidAmountLimit
              ? Math.min(amount, this.effectiveBidAmountLimit)
              : amount;
            return this.negotiationsMinimum && effectiveAmount < this.negotiationsMinimum
              ? { negotiationsMinimum: { min: this.negotiationsMinimum, actual: effectiveAmount } }
              : null;
          },
        ],
      ],
      comment: [''],
      dumpVersion: [''],
      lotId: [''],
      lotType: [this.lotType],
      conditionId: [null, [Validators.required]],
      groupId: [null],
      createTranslation: [false],
      notify: [false],
      commissionAgreement: [false],
      agreement: [true],
      needPriorityPass: [false],
      isGreenCorner: [false],
    });
  }

  openPriorityPassInfoModal(): void {
    this.modalService.create({
      nzTitle: this.translate.instant('LOT_BIDDING_MODAL.NEED_PRIORITY_PASS'),
      nzContent: PriorityPassInfoComponent,
    });
  }

  setDisassemblyState(isDisassembly: boolean): void {
    this.isDisassembly = isDisassembly;
  }

  resetForm(defaultBidCondition?: BidCondition, createTranslation = false): void {
    this.form.reset({
      dumpVersion: this.lot.dumpVersion,
      lotId: this.lot.id,
      amount: this.negotiationsMinimum || this.lot.startPrice || 0,
      lotType: this.lotType,
      createTranslation: createTranslation,
      conditionId: defaultBidCondition?.id ? +defaultBidCondition.id : null,
      groupId: null,
      comment: '',
      notify: false,
      agreement: true,
      commissionAgreement: false,
      needPriorityPass: false,
      isGreenCorner: false,
    });
  }

  createNewGroup(event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.form.contains('newGroup')) {
      return;
    }

    const newGroup = this.fb.group({
      date: [
        DateTime.fromISO(this.lot.time, { zone: 'Asia/Tokyo' }).startOf('day').toISO(),
        Validators.required,
      ],
      letter: [null, Validators.required],
      quantity: [1, [Validators.min(1), Validators.max(9)]],
    });

    this.form.addControl('newGroup', newGroup);
    this.isNewGroup = true;
    this.cdr.detectChanges();

    if (this.lotBiddingModalGuideService.isActive) {
      this.lotBiddingModalGuideService.handleNewGroupCreated();
    }
  }

  removeNewGroup(options: RemoveNewGroupOptions = {}, event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.form.contains('newGroup')) {
      this.form.removeControl('newGroup');
    }

    this.isNewGroup = false;
    this.cdr.detectChanges();

    if (options.notifyGuide !== false && this.lotBiddingModalGuideService.isActive) {
      this.lotBiddingModalGuideService.handleNewGroupRemoved(
        options.guideTarget ?? 'afterGrouping',
      );
    }
  }

  startGuide(): void {
    this.biddingDataQueryRef.data.pipe(take(1)).subscribe((bidData) => {
      const countryIso = bidData?.currentUser?.countryIso?.toLowerCase();

      this.cdr.detectChanges();

      this.lotBiddingModalGuideService.start({
        isCanGroupBids: bidData?.isCanGroupBids ?? false,
        isNewGroup: () => this.isNewGroup,
        renderCalculator: this.renderCalculator,
        isOtherCountriesUser: bidData?.currentUser?.isOtherCountries ?? false,
        canCreateTranslation: this.canCreateTranslation,
        isSanctioned: bidData?.isSanctioned ?? false,
        isRussianUser: countryIso === 'ru',
        removeNewGroup: (options) => this.removeNewGroup(options),
      });
    });
  }

  async checkBirthdateAndPlaceBid(): Promise<void> {
    const currentUser = await this.biddingDataQueryRef.data
      .pipe(take(1), pluck('currentUser'))
      .toPromise();

    if (currentUser.birthdate) {
      await this.placeBid();
    } else {
      this.modalService
        .create({
          nzTitle: this.translate.instant('BIRTHDATE_MODAL.TITLE'),
          nzContent: SetBirthdateModalComponent,
          nzClosable: false,
          nzMaskClosable: false,
          nzComponentParams: {
            currentUserId: currentUser.id,
          },
        })
        .afterClose.subscribe(async (result) => {
          if (result) {
            await this.placeBid();
          }
        });
    }
  }

  async placeBid(): Promise<void> {
    if (isLotTradingClosed(this.lot)) {
      this.notification.renderError('NEGOTIATIONS.TRADING_CLOSED');
      return;
    }

    validateAllFormFields(this.form);

    const value = this.form.getRawValue();

    if (this.form.valid) {
      if (+value.amount % 5000 !== 0) {
        this.notification.renderError('LOT_BIDDING_MODAL.BID_MUST_BE_ROUND');
        return;
      }

      const placingBidObservable = of(this.isNewGroup).pipe(
        switchMap((isNewGroup) =>
          iif(() => isNewGroup, this.createGroup(value.newGroup), of(value.groupId || null)),
        ),
        switchMap((groupId) =>
          this.createBid({
            amount: value.amount,
            comment: value.comment,
            conditionId: value.conditionId,
            createTranslation: value.createTranslation,
            lotId: value.lotId,
            lotType: value.lotType,
            notify: value.notify,
            lotSource: this.lotSource,
            withLimitsOverride: this.withLimitsOverride,
            needPriorityPass: value.needPriorityPass,
            isGreenCorner: value.isGreenCorner,
            groupId,
          }),
        ),
        switchMap((bidCreatePayload) =>
          iif(
            () => this.canRecognizeAuctionList && !this.hasTranslations,
            this.createAutoTranslation().pipe(map(() => bidCreatePayload)),
            of(bidCreatePayload),
          ),
        ),
      );

      const reference = this.ohs.toReference(placingBidObservable);
      this.isPlacingBid$ = reference.loading;
      reference.data.subscribe((bidCreatePayload) => {
        const error = bidCreatePayload?.error;
        if (error) {
          if (error?.code === 'InsufficientOverbiddingAmount') {
            this.notification.renderError(
              'LOT_BIDDING_MODAL.ERRORS.INSUFFICIENT_OVERBIDDING_AMOUNT.TITLE',
              'LOT_BIDDING_MODAL.ERRORS.INSUFFICIENT_OVERBIDDING_AMOUNT.MESSAGE',
            );
          } else {
            this.notification.renderError('LOT_BIDDING_MODAL.FAIL_CREATE_BID', error.message);
          }
          return;
        }

        this.afterSubmit.emit(true);
        this.modal.destroy();
        this.notification.renderSuccess(
          'Success',
          this.buildHintNotification(bidCreatePayload.hint),
        );
      });
    } else {
      this.notification.renderError('LOT_BIDDING_MODAL.FILL_ALL_FIELDS');
    }
  }

  private createGroup(input: BidGroupCreateInput): Observable<string> {
    return this.mhs
      .handle(
        this.bidGroupCreateGQL,
        { input },
        {
          failureTranslationKey: 'LOT_BIDDING_MODAL.FAIL_CREATE_GROUP',
          successTranslationKey: 'LOT_BIDDING_MODAL.SUCCESS_CREATE_GROUP',
        },
      )
      .pipe(map((data) => data.bidGroupCreate?.group?.id));
  }

  private createBid(input: BidCreateInput): Observable<BidCreatePayload> {
    return this.bidCreateGQL.mutate({ input }).pipe(map((result) => result.data.bidCreate));
  }

  private createAutoTranslation(): Observable<void> {
    return this.mhs
      .handle(
        this.lotAutoTranslationCreateGQL,
        {
          input: {
            lotId: this.lot.id,
            lotType: this.lotType,
            lotSource: this.lotSource,
          },
        },
        {
          renderError: false,
          renderSuccess: false,
        },
      )
      .pipe(
        map(() => {}),
        catchError(() => of({} as unknown as void)),
      );
  }

  private buildHintNotification(hint?: PlaceBidHint): string {
    if (hint) {
      return this.translate.instant(hint.text, { triesLeft: hint.triesLeft });
    } else {
      return 'LOT_BIDDING_MODAL.SUCCESS_CREATE_BID';
    }
  }

  getBidGroupLabel(group: BidGroup): string {
    const base = `${group.letter} - ${group.quantity}`;
    const limitText = this.translate.instant('LOT_BIDDING_MODAL.LIMIT');

    if (group.lotsLimitReached && !this.currentUserLastBid) {
      return `${base} (${limitText})`;
    } else {
      return base;
    }
  }

  notificationSettings(): void {
    this.modalService.create({
      nzTitle: this.translate.instant('SETTINGS.TITLE'),
      nzContent: SettingsModalComponent,
      nzComponentParams: {
        selectedIndex: 2,
      },
    });
  }

  ngOnDestroy(): void {
    this.lotBiddingModalGuideService.forceClose();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
